import { useEffect, useRef, useState } from "react";
import { Play, Pause, X, Loader2, Volume2 } from "lucide-react";
import { API } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const VOICES = [
  { v: "fable",   label: "fable — storyteller" },
  { v: "onyx",    label: "onyx — deep" },
  { v: "nova",    label: "nova — bright" },
  { v: "shimmer", label: "shimmer — warm" },
  { v: "alloy",   label: "alloy — neutral" },
  { v: "echo",    label: "echo — calm" },
  { v: "sage",    label: "sage — measured" },
];

export default function AudioPlayer({ pageText, pageIndex, onClose, onNextPage }) {
  const audioRef = useRef(null);
  const [voice, setVoice] = useState("fable");
  const [speed, setSpeed] = useState(1);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const abortRef = useRef(null);

  // Whenever page changes, wipe existing audio
  useEffect(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  const fetchAudio = async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      // strip paragraph delims, cap at 3800 chars for safety
      const text = pageText.replace(/\n+/g, " ").slice(0, 3800);
      const t = localStorage.getItem("pba_token");
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        signal: ctrl.signal,
        headers: {
          "Content-Type": "application/json",
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        body: JSON.stringify({ text, voice, speed: 1.0 }), // speed handled client-side via playbackRate
      });
      if (!res.ok) throw new Error(`tts failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      return url;
    } catch (e) {
      if (e.name !== "AbortError") toast.error("couldn't generate narration");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = async () => {
    let url = audioUrl;
    if (!url) url = await fetchAudio();
    if (!url) return;
    const el = audioRef.current;
    if (!el) return;
    el.playbackRate = speed;
    if (el.paused) { await el.play(); setPlaying(true); }
    else { el.pause(); setPlaying(false); }
  };

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  // When voice changes, wipe cached audio
  useEffect(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice]);

  const onEnded = () => {
    setPlaying(false);
    if (onNextPage) onNextPage();
  };

  return (
    <div
      data-testid="audio-player"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[min(760px,calc(100%-2rem))] rounded-full paper-shadow border border-border bg-card px-4 py-3 flex items-center gap-3"
    >
      <button
        onClick={togglePlay}
        disabled={loading}
        data-testid="audio-play-pause-btn"
        className="w-11 h-11 rounded-full bg-primary text-primary-foreground grid place-items-center hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="font-ui text-xs tracking-caps text-muted-foreground">narration</div>
        <div className="font-display text-sm truncate">
          {loading ? "warming up the narrator…" : playing ? "reading page" : audioUrl ? "paused" : "press play to listen"}
        </div>
      </div>

      <div className="hidden sm:flex items-center gap-2">
        <Volume2 className="w-3.5 h-3.5 text-muted-foreground" />
        <Select value={voice} onValueChange={setVoice}>
          <SelectTrigger className="h-9 w-36 font-ui text-xs rounded-full" data-testid="audio-voice-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VOICES.map((v) => <SelectItem key={v.v} value={v.v}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-1 bg-secondary rounded-full p-1">
        {SPEEDS.map((s) => (
          <button
            key={s}
            onClick={() => setSpeed(s)}
            data-testid={`audio-speed-${s}x`}
            className={`font-ui text-[11px] px-2.5 py-1 rounded-full transition-colors ${
              speed === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {s}x
          </button>
        ))}
      </div>

      <button
        onClick={() => {
          if (audioRef.current) { audioRef.current.pause(); }
          if (audioUrl) URL.revokeObjectURL(audioUrl);
          onClose();
        }}
        data-testid="audio-close-btn"
        className="w-9 h-9 rounded-full hover:bg-secondary grid place-items-center"
      >
        <X className="w-4 h-4" />
      </button>

      <audio
        ref={audioRef}
        src={audioUrl || undefined}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={onEnded}
        preload="auto"
      />
    </div>
  );
}
